// ============================================================================
// Connection Wizard
// ============================================================================
// 4-step wizard for setting up a new integration connection:
//   Step 1: Select Provider & Enter Credentials
//   Step 2: Test Credentials
//   Step 3: Map Location
//   Step 4: Initial Sync

import { useState } from 'react'
import { Loader2, CheckCircle2, XCircle, ArrowRight, ArrowLeft } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useTenant } from '@/contexts'
import {
  useIntegrationProviders,
  useTestCredentials,
  useCreateCredentialSet,
  useMapLocation,
  useInitialSync,
} from '@/hooks/useIntegrations'

interface ConnectionWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type WizardStep = 'credentials' | 'test' | 'location' | 'sync'

export function ConnectionWizard({ open, onOpenChange }: ConnectionWizardProps) {
  const {  locations } = useTenant()
  const { data: providers } = useIntegrationProviders()

  // Form state
  const [step, setStep] = useState<WizardStep>('credentials')
  const [provider, setProvider] = useState('')
  const [label, setLabel] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [locationId, setLocationId] = useState('')
  const [fboCode, setFboCode] = useState('')

  // Created IDs (passed between steps)
  const [credentialSetId, setCredentialSetId] = useState('')
  const [credentialId, setCredentialId] = useState('')

  // Mutations
  const testCreds = useTestCredentials()
  const createCreds = useCreateCredentialSet()
  const mapLoc = useMapLocation()
  const initialSync = useInitialSync()

  const activeProviders = providers?.filter(p =>
    p.code !== 'manual' && p.provider_type === 'reservation',
  ) ?? []

  const reset = () => {
    setStep('credentials')
    setProvider('')
    setLabel('')
    setUsername('')
    setPassword('')
    setLocationId('')
    setFboCode('')
    setCredentialSetId('')
    setCredentialId('')
    testCreds.reset()
    createCreds.reset()
    mapLoc.reset()
    initialSync.reset()
  }

  const handleClose = () => {
    onOpenChange(false)
    setTimeout(reset, 300)
  }

  // Step 1 → Step 2: Test credentials
  const handleTestCredentials = async () => {
    setStep('test')
    const result = await testCreds.mutateAsync({ provider, username, password })

    if (result.success) {
      // Auto-create credential set on successful test
      const createResult = await createCreds.mutateAsync({
        provider,
        label,
        username,
        password,
      })
      setCredentialSetId(createResult.credential_set_id)
    }
  }

  // Step 3: Map location
  const handleMapLocation = async () => {
    const result = await mapLoc.mutateAsync({
      credentialSetId,
      locationId,
      providerConfig: fboCode ? { fboCode } : undefined,
    })
    setCredentialId(result.credential_id)
    setStep('sync')
  }

  // Step 4: Initial sync
  const handleInitialSync = async () => {
    await initialSync.mutateAsync(credentialId)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Connect Integration</DialogTitle>
          <DialogDescription>
            {step === 'credentials' && 'Enter your provider credentials to get started.'}
            {step === 'test' && 'Testing your credentials...'}
            {step === 'location' && 'Map this connection to one of your locations.'}
            {step === 'sync' && 'Run the initial sync to import reservations.'}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center gap-1 mb-4">
          {(['credentials', 'test', 'location', 'sync'] as const).map((s, i) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= ['credentials', 'test', 'location', 'sync'].indexOf(step)
                  ? 'bg-primary'
                  : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Credentials */}
        {step === 'credentials' && (
          <div className="space-y-4">
            <div>
              <Label>Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {activeProviders.map((p) => (
                    <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Connection Label</Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., FlightBridge Production"
              />
            </div>
            <div>
              <Label>Username</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="API username"
              />
            </div>
            <div>
              <Label>Password / API Key</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="API password or key"
              />
            </div>
            <Button
              className="w-full"
              disabled={!provider || !label || !username || !password}
              onClick={handleTestCredentials}
            >
              Test & Save Credentials
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Test Result */}
        {step === 'test' && (
          <div className="flex flex-col items-center gap-4 py-6">
            {testCreds.isPending ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Testing credentials...</p>
              </>
            ) : testCreds.isError || (testCreds.data && !testCreds.data.success) ? (
              <>
                <XCircle className="h-8 w-8 text-destructive" />
                <p className="text-sm text-destructive">
                  {testCreds.error?.message ?? testCreds.data?.message ?? 'Authentication failed'}
                </p>
                <Button variant="outline" onClick={() => setStep('credentials')}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </>
            ) : createCreds.isPending ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Saving credentials...</p>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
                <p className="text-sm text-green-600">Credentials verified and saved</p>
                <Button onClick={() => setStep('location')}>
                  Map Location
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </>
            )}
          </div>
        )}

        {/* Step 3: Location Mapping */}
        {step === 'location' && (
          <div className="space-y-4">
            <div>
              <Label>Location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {(locations ?? []).map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>FBO Code (optional)</Label>
              <Input
                value={fboCode}
                onChange={(e) => setFboCode(e.target.value)}
                placeholder="Provider-specific FBO identifier"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Some providers require an FBO code to filter reservations.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep('test')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                className="flex-1"
                disabled={!locationId || mapLoc.isPending}
                onClick={handleMapLocation}
              >
                {mapLoc.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Map Location
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Initial Sync */}
        {step === 'sync' && (
          <div className="flex flex-col items-center gap-4 py-6">
            {initialSync.isPending ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Running initial sync...</p>
              </>
            ) : initialSync.isSuccess ? (
              <>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
                <p className="text-sm text-green-600">Integration connected and synced</p>
                <Button onClick={handleClose}>Done</Button>
              </>
            ) : initialSync.isError ? (
              <>
                <XCircle className="h-8 w-8 text-destructive" />
                <p className="text-sm text-destructive">{initialSync.error?.message}</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => initialSync.reset()}>
                    Retry
                  </Button>
                  <Button onClick={handleClose}>Skip & Finish</Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground text-center">
                  Connection is ready. Run the initial sync to import existing reservations
                  from the provider.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleClose}>
                    Skip for Now
                  </Button>
                  <Button onClick={handleInitialSync}>
                    Run Initial Sync
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
